// src/writer/components/AIToolsView.tsx
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, MessageSquare, FileText, Activity } from 'lucide-react';

// Phase 2 AI Components
import { ContextPanel } from '../../components/ai/ContextPanel';
import { SmartSuggestions } from '../../components/ai/SmartSuggestions';
import { StoryHealthDashboard } from '../../components/analysis/StoryHealthDashboard';
import { GrammarChecker } from '../../components/tools/GrammarChecker';

interface AIToolsViewProps {
  projectId: number;
  projectState: any;
  onProjectStateChange?: (updates: any) => void;
}

const AIToolsView: React.FC<AIToolsViewProps> = ({ 
  projectId, 
  projectState, 
  onProjectStateChange 
}) => {
  const [activeTab, setActiveTab] = useState('context');

  const handleContextUpdate = () => {
    // Trigger any necessary updates when context changes
    if (onProjectStateChange) {
      onProjectStateChange({ lastContextUpdate: Date.now() });
    }
  };

  return (
    <div className="w-full h-full bg-white p-6 rounded-lg shadow-xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">AI Writing Tools</h1>
        <p className="text-gray-600">
          Advanced AI-powered tools to enhance your writing and story development
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="context" className="flex items-center gap-2">
            <Brain className="h-4 w-4" />
            Story Context
          </TabsTrigger>
          <TabsTrigger value="suggestions" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Smart Suggestions
          </TabsTrigger>
          <TabsTrigger value="health" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Story Health
          </TabsTrigger>
          <TabsTrigger value="grammar" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Grammar & Style
          </TabsTrigger>
        </TabsList>

        <TabsContent value="context" className="mt-6 h-full">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-blue-600" />
                Story Context Engine
              </CardTitle>
              <p className="text-sm text-gray-600">
                AI-powered analysis of your story elements, characters, and world-building
              </p>
            </CardHeader>
            <CardContent className="h-full">
              <ContextPanel 
                projectId={projectId}
                onContextUpdate={handleContextUpdate}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suggestions" className="mt-6 h-full">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-green-600" />
                Smart Writing Suggestions
              </CardTitle>
              <p className="text-sm text-gray-600">
                Context-aware suggestions to improve your writing and story consistency
              </p>
            </CardHeader>
            <CardContent className="h-full">
              <SmartSuggestions 
                projectId={projectId}
                currentText={projectState?.activeChapter?.content || ''}
                onSuggestionApplied={handleContextUpdate}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="health" className="mt-6 h-full">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-purple-600" />
                Story Health Dashboard
              </CardTitle>
              <p className="text-sm text-gray-600">
                Comprehensive analysis of your story's structure, pacing, and development
              </p>
            </CardHeader>
            <CardContent className="h-full">
              <StoryHealthDashboard 
                projectId={projectId}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="grammar" className="mt-6 h-full">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange-600" />
                Grammar & Style Analysis
              </CardTitle>
              <p className="text-sm text-gray-600">
                Advanced grammar checking and style enhancement for creative writing
              </p>
            </CardHeader>
            <CardContent className="h-full">
              <GrammarChecker 
                initialText={projectState?.activeChapter?.content || ''}
                onTextChange={handleContextUpdate}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIToolsView;